<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import { DateTime } from 'luxon'

    import { gettext as _, ngettext, resolve, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import Date from 'hydria/components/Date.svelte'
    import PageList from 'hydria/components/PageList.svelte'

    const LIMIT = 10
    type States = '' | 'SUCCESS' | 'FAILED' | 'FAILED-simple' | 'RUNNING' | 'READY'

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        skip: 'int1+',
        owner: 'string',
        affected_id: 'string',
        limit_to_days: 'number',
        state: 'string',
        hidden: 'string',
    }
    const mappingOrder = ['skip']

    // Search options for the backend
    interface SOptions {
        skip: number
        owner: string
        affected_id: string
        limit_to_days: number | null
        state: States
        hidden: 'all' | 'hidden' | 'visible'
    }
    const defaults: SOptions = {
        skip: 0,
        owner: '',
        affected_id: '',
        limit_to_days: null,
        state: '',
        hidden: 'visible',
    }

    // Search result from the server
    interface ISearchResult {
        items: Array<any>
        total_items: number
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        if (options.skip && options.skip == loadedOptions.skip) {
            // Something non-skip must have changed, so we force skip to 0
            options.skip = 0
        }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/tasks' : `/tasks?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/tasks' : `/tasks?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string) {
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let results = writable([] as any[])
    let autoHidableTaskIds = $state([] as string[])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = { limit: LIMIT }
        if (sOptions.skip > 0) sdata.skip = sOptions.skip
        for (const [field, value] of Object.entries(sOptions)) {
            if (['skip'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (field == 'hidden') {
                        sdata[field] = value == 'visible' ? false : value == 'hidden' ? true : null
                    } else if (value != null && value != '') sdata[field] = value
                    break
                case 'number':
                    if (value != null && value != '' && value != 0) sdata[field] = `${value}`
                    break
            }
        }

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/task/1/', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = $state(0)

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []
        let ahtIds: string[] = []

        const now = DateTime.now()

        nItems = data.total_items

        for (const item of data.items) {
            for (const key of ['ctime', 'etime']) {
                if (item[key]) item[key] = toLuxon(item[key])
            }

            litems.push(item)

            if (['update_agreement', 'poll_cira_messages'].includes(item['request_type'])) {
                ahtIds.push(item['id'])
            }
        }

        results.set(litems)
        autoHidableTaskIds = ahtIds
        if (loading) loading = false
    }

    let textOwner: string = $state('')
    let textAffectedId: string = $state('')
    function loadIntoForm() {
        textOwner = sOptions.owner
        textAffectedId = sOptions.affected_id
    }

    const taskStates: {
        tag: States
        title: string
        icon: string
    }[] = [
        { tag: 'SUCCESS', title: 'Succès', icon: 'fa:check' },
        { tag: 'FAILED-simple', title: 'Erreurs sans update_local', icon: 'fa:times' },
        { tag: 'FAILED', title: 'Erreurs (incluant update_local)', icon: 'fa:times-circle' },
        { tag: 'RUNNING', title: 'En exécution', icon: 'fa:cog' },
        { tag: 'READY', title: 'Prêt à exécuter (en attente d’un worker)', icon: 'fa:thumbs-o-up' },
    ]

    function submit() {
        if (sOptions.owner !== textOwner) sOptions.owner = textOwner
        if (sOptions.affected_id !== textAffectedId) sOptions.affected_id = textAffectedId
        setQueryString(sOptions)
    }

    let hidingAll = $state(false)
    async function hideAllErrors() {
        if (hidingAll) return
        let r: SimpleFetchResult
        hidingAll = true
        try {
            r = await simpleFetch('POST', '/json/task/1/hide-all-errors')
        } catch (e: any) {
            r = e
            notify({ title: $_('Cacher les tâches'), text: $_('Une erreur s’est produite'), type: 'danger' })
            hidingAll = false
            return
        }
        notify({
            type: 'success',
            title: 'Cacher les tâches',
            text: r.data.message,
        })
        hidingAll = false
        doSearch()
    }

    async function hideAllCira() {
        if (hidingAll) return
        let r: SimpleFetchResult
        hidingAll = true
        try {
            r = await simpleFetch('POST', '/json/task/1/hide-all-cira')
        } catch (e: any) {
            r = e
            notify({ title: $_('Cacher CIRA'), text: $_('Une erreur s’est produiete'), type: 'danger' })
            hidingAll = false
            return
        }

        notify({
            type: 'success',
            title: 'Cacher CIRA',
            text: r.data.message,
        })
        hidingAll = false
        doSearch()
    }

    async function hideAutoIds() {
        if (hidingAll || autoHidableTaskIds.length < 1) return

        let r: SimpleFetchResult
        hidingAll = true
        try {
            r = await simpleFetch('POST', '/json/task/1/hide-many', { task_ids: $state.snapshot(autoHidableTaskIds) })
        } catch (e: any) {
            r = e
            notify({ title: $_('Cacher CIRA'), text: $_('Une erreur s’est produiete'), type: 'danger' })
            hidingAll = false
            return
        }

        notify({
            type: 'success',
            title: 'Cacher tâches',
            text: r.data.message,
        })
        hidingAll = false
        doSearch()
    }
</script>

<style>
    td {
        color: var(--bs-table-color);
    }
    .task.danger {
        --bs-table-striped-color: white;
        --bs-table-color: white;
        --bs-table-bg: var(--bs-danger);
        --bs-link-color: white;
        --bs-link-hover-color: white;
    }
    .task.warning {
        --bs-table-bg: var(--bs-warning);
        --bs-link-color: var(--bs-body-color);
        --bs-link-hover-color: var(--bs-body-color);
    }
    .task.info {
        --bs-table-bg: var(--bs-info);
        --bs-link-color: var(--bs-body-color);
        --bs-link-hover-color: var(--bs-body-color);
    }
</style>

<Breadcrumb crumbs={[{ txt: tgettext('Tasks') }]} />

<div class="card mb-4">
    <div class="card-title text-primary">
        <div>{$_('Search parameters')}</div>
        <div>
            <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                disabled={loading || hidingAll || autoHidableTaskIds.length < 1}
                onclick={() => {
                    hideAutoIds()
                }}>{$ngettext('Cacher {n} “CIRA” de base', 'Cacher {n} “CIRA” de base', { n: autoHidableTaskIds.length })}</button
            >
            <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                disabled={loading || hidingAll}
                onclick={() => {
                    hideAllErrors()
                }}>Cacher toutes les tâches en erreur</button
            >
        </div>
    </div>
    <div class="card-body">
        <form
            onsubmit={(e) => {
                e.preventDefault()
                submit()
            }}
            class="form"
        >
            <div class="d-flex flex-wrap gap-2">
                <div style="flex: 3 0 12rem;">
                    <div class="input-group">
                        <span class="input-group-text">{$_('Owner:')}</span>
                        <input type="text" class="form-control" placeholder={$_('Filter results by owner ID…')} bind:value={textOwner} disabled={updating} />
                        {#if textOwner}
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                onclick={() => {
                                    textOwner = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                    </div>
                </div>
                <div style="flex: 3 0 12rem;">
                    <div class="input-group">
                        <span class="input-group-text">{$_('Item’s ID:')}</span>
                        <input type="text" class="form-control" placeholder={$_('Filter results by affected ID (domain, contact, …)')} bind:value={textAffectedId} disabled={updating} />
                        {#if textAffectedId}
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                onclick={() => {
                                    textAffectedId = ''
                                    submit()
                                }}><Icon inline icon="fa:times" /></button
                            >
                        {/if}
                    </div>
                </div>
                <div style="flex: 2 0 8rem;">
                    <div class="input-group">
                        <span class="input-group-text">{$_('Days:')}</span>
                        <input type="text" class="form-control" placeholder={$_('limit to n days')} bind:value={sOptions.limit_to_days} disabled={updating} />
                    </div>
                </div>
                <div>
                    <div class="btn-group">
                        {#each taskStates as state}
                            <button
                                class="btn btn-outline-secondary"
                                type="button"
                                data-tooltip={state.title}
                                class:active={sOptions.state === state.tag}
                                onclick={() => {
                                    sOptions.state = sOptions.state === state.tag ? '' : state.tag
                                    setQueryString(sOptions)
                                }}
                                disabled={updating}><Icon inline icon={state.icon} /></button
                            >
                        {/each}
                    </div>
                    <div class="btn-group">
                        <button
                            class="btn btn-outline-secondary"
                            type="button"
                            data-tooltip={$_('All')}
                            class:active={sOptions.hidden == 'all'}
                            onclick={() => {
                                sOptions.hidden = 'all'
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:asterisk" /></button
                        >
                        <button
                            class="btn btn-outline-secondary"
                            type="button"
                            data-tooltip={$_('Visible')}
                            class:active={sOptions.hidden == 'visible'}
                            onclick={() => {
                                sOptions.hidden = 'visible'
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:eye" /></button
                        >
                        <button
                            class="btn btn-outline-secondary"
                            type="button"
                            data-tooltip={$_('Hidden')}
                            class:active={sOptions.hidden == 'hidden'}
                            onclick={() => {
                                sOptions.hidden = 'hidden'
                                setQueryString(sOptions)
                            }}><Icon inline icon="fa:eye-slash" /></button
                        >
                    </div>
                </div>
                <div class="ps-2">
                    <div class="float-end">
                        <button class="btn btn-primary" type="submit" disabled={updating}>{$_('Filter')}</button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

{#if loading}
    <div class="loading">
        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
        {$_('Loading…')}
    </div>
{:else}
    <div class="card">
        <div class="card-title text-primary">{$_('Domains')}</div>

        {#if $results.length}
            <div class="card-body">
                <table class="table table-striped mb-0" class:disabled-look={updating}>
                    <thead>
                        <tr>
                            <th>{$_('Request name')}</th>
                            <th class="col-md-2">{$_('State')}</th>
                            <th class="col-md-1"></th>
                            <th class="col-md-2">{$_('Created on')}</th>
                            <th class="col-md-2">{$_('End')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each $results as item (item.id)}
                            <tr class="task" class:info={item.state == 'RUNNING' || item.state == 'READY'} class:danger={item.state == 'FAILED'} class:warning={!['RUNNING', 'READY', 'FAILED', 'SUCCESS'].includes(item.state)}>
                                <td><a use:sanchor href={spath(`/task/${item.id}`)}>{$resolve(item.name, item.name_args)}</a></td>
                                <td>{item.state}</td>
                                <td
                                    >{#if item.hidden}<Icon inline icon="fa:eye-slash" />{/if}</td
                                >
                                <td style="white-space: nowrap"><Date value={item.ctime} format="h-table-s" /></td>
                                <td style="white-space: nowrap"><Date value={item.etime} format="h-table-s" /></td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
        <div class="card-footer">
            <PageList
                loading={updating}
                {nItems}
                limit={LIMIT}
                skip={sOptions.skip}
                setSkip={(e) => {
                    sOptions.skip = e
                    setQueryString(sOptions)
                }}
            />
        </div>
    </div>
{/if}
